import { Form, Formik, useFormikContext } from "formik";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ReactComponent as ClipboardIcon } from "assets/images/common/clipboard.svg";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import CertificateUpload from "components/CertificateUpload";
import DatePicker from "components/DatePicker";
import SearchableSelect from "components/SearchableSelect";
import Select from "components/Select";
import TextInput from "components/TextInput";

import useValidation from "hooks/useValidation";

import { AssetsApi, CompaniesApi, DevicesApi, PortfoliosApi } from "services/http";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { BaseOption } from "entities/common";
import {
  CALIBRATION_INTERVAL_OPTIONS,
  DEVICE_APP_KEY_DEFAULT,
  DEVICE_EUI_LENGTH,
  REPORTING_INTERVAL_DEFAULT,
  getReportingOptionsForModel,
  isAppKeyAutofillModel,
  sanitizeAppKeyInput,
} from "entities/devices";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getDeviceProfilesRequest } from "store/slices/deviceSlice";

import { UserRole } from "entities/common";

import { buildValidationSchema } from "../validationSchema";

import styles from "../styles.module.scss";

export type DeviceFormValues = {
  companyId: string;
  companyLabel?: string;
  portfolioId: string;
  portfolioLabel?: string;
  assetId: string;
  assetLabel?: string;
  installationLocation: string;
  deviceProfileKey: string;
  deviceModelLabel?: string;
  devEui: string;
  appKey: string;
  reportingInterval: string;
  lastCalibrationDate: string;
  calibrationInterval: string;
  certificate: File | null;
};

type Mode = "create" | "edit";

type PropTypes = {
  onClose?: () => void;
  onSubmit?: (values: DeviceFormValues) => void;
  // FR 7.2.2 — fired when user clicks [Update] in edit mode. Receives the
  // immutable dev_eui (used as URL key for PATCH) plus the full form values
  // so the caller can extract just the editable subset for the API payload.
  onUpdate?: (devEui: string, values: DeviceFormValues) => void;
  mode?: Mode;
  description?: string;
  initialData?: Partial<DeviceFormValues>;
  isLoading?: boolean;
  submitError?: string | null;
};

type FormFieldsProps = {
  onCancel: () => void;
  mode: Mode;
  isLoading?: boolean;
  submitError?: string | null;
};

const SELECT_PAGE_SIZE = 50;

const CALIBRATION_OPTIONS: BaseOption[] = CALIBRATION_INTERVAL_OPTIONS.map((o) => ({
  value: o.value,
  label: o.label,
}));

const EUI_UPLOAD_TOOLTIP =
  "Upload a close-up photo of the EUI label. Make sure the label is clearly visible and in focus";
const APP_KEY_UPLOAD_TOOLTIP =
  "Upload a close-up photo of the AppKey label. Make sure the label is clearly visible and in focus";

const EUI_IMAGE_ACCEPT = "image/jpeg,image/png,image/webp";
const EUI_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
const EUI_INVALID_FILE_MSG =
  "Unable to upload image. Allowed formats: JPEG, PNG, WEBP. Max size: 5 MB.";
const EUI_OCR_FAIL_MSG = "Unable to recognize Device EUI. Please try again.";
const APP_KEY_OCR_FAIL_MSG = "Unable to recognize Device AppKey. Please try again.";
const LOCKED_FIELD_TOOLTIP = "Cannot be changed after registration. To replace — delete and re-add";

const defaultInitialValues: DeviceFormValues = {
  companyId: "",
  companyLabel: "",
  portfolioId: "",
  portfolioLabel: "",
  assetId: "",
  assetLabel: "",
  installationLocation: "",
  deviceProfileKey: "",
  devEui: "",
  appKey: "",
  reportingInterval: "",
  lastCalibrationDate: "",
  calibrationInterval: "12",
  certificate: null,
};

const FormFields: FC<FormFieldsProps> = ({ onCancel, mode, isLoading, submitError }) => {
  const dispatch = useAppDispatch();
  const formRef = useRef<HTMLDivElement>(null);
  const euiPhotoRef = useRef<HTMLInputElement>(null);
  const appKeyPhotoRef = useRef<HTMLInputElement>(null);

  const isCreate = mode === "create";
  const profiles = useAppSelector((state: RootStore) => state.devices.profiles);
  const authUser = useAppSelector((state: RootStore) => state.auth.user);
  const isAdmin = authUser?.role === UserRole.ADMINISTRATOR;

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    handleChange,
    handleBlur,
    handleSubmit,
  } = useFormikContext<DeviceFormValues>();

  const [tooltip, setTooltip] = useState<{
    content: string;
    left: number;
    top: number;
    placement: "right" | "below";
  } | null>(null);

  useValidation(formRef, errors);

  useEffect(() => {
    if (profiles.length === 0) {
      dispatch(getDeviceProfilesRequest());
    }
  }, [dispatch, profiles.length]);

  const profileOptions = useMemo<BaseOption[]>(() => {
    const options = profiles.map((p) => ({ value: p.profile_key, label: p.device_model }));
    const current = values.deviceProfileKey;
    if (current && values.deviceModelLabel && !options.some((o) => o.value === current)) {
      options.push({ value: current, label: values.deviceModelLabel });
    }
    return options;
  }, [profiles, values.deviceProfileKey, values.deviceModelLabel]);

  const reportingOptions = useMemo<BaseOption[]>(
    () => getReportingOptionsForModel(values.deviceProfileKey, values.reportingInterval),
    [values.deviceProfileKey, values.reportingInterval],
  );

  const skipFirstReportingReset = useRef(true);
  useEffect(() => {
    if (skipFirstReportingReset.current) {
      skipFirstReportingReset.current = false;
      return;
    }
    if (!isCreate || !values.deviceProfileKey) return;
    setFieldValue("reportingInterval", REPORTING_INTERVAL_DEFAULT);
  }, [values.deviceProfileKey, isCreate, setFieldValue]);

  const showTooltip = useCallback((element: HTMLElement, content: string) => {
    const rect = element.getBoundingClientRect();
    const dialog = document.querySelector("dialog[open]");
    if (!dialog) return;
    const dialogRect = dialog.getBoundingClientRect();

    const GAP = 12;
    const TOOLTIP_MAX_WIDTH = 240;
    const VIEWPORT_PADDING = 8;
    const fitsRight = rect.right + GAP + TOOLTIP_MAX_WIDTH <= window.innerWidth - VIEWPORT_PADDING;

    setTooltip(
      fitsRight
        ? {
            content,
            left: rect.right + GAP - dialogRect.left,
            top: rect.top + rect.height / 2 - dialogRect.top,
            placement: "right",
          }
        : {
            content,
            left: rect.right - dialogRect.left,
            top: rect.bottom + GAP - dialogRect.top,
            placement: "below",
          },
    );
  }, []);

  const hideTooltip = useCallback(() => setTooltip(null), []);

  const searchCompanies = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
        }),
      );
      const options: BaseOption[] = (data?.items ?? []).map((c) => ({
        value: String(c.id),
        label: c.name,
      }));
      return { options, hasMore: (data?.total ?? 0) > page * SELECT_PAGE_SIZE };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const searchPortfolios = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await PortfoliosApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(values.companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(values.companyId) } : {}),
          }),
        );
        const options: BaseOption[] = (data?.items ?? []).map((p) => ({
          value: String(p.id),
          label: p.name,
          companyId: p.company_id,
          companyName: p.company_name,
        }));
        return { options, hasMore: (data?.total ?? 0) > page * SELECT_PAGE_SIZE };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [values.companyId],
  );

  const searchAssets = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await AssetsApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(values.portfolioId
              ? { [QUERY_PARAMS.PORTFOLIO_ID]: Number(values.portfolioId) }
              : {}),
            ...(values.companyId
              ? { [QUERY_PARAMS.COMPANY_ID]: Number(values.companyId) }
              : {}),
          }),
        );
        let options: BaseOption[] = (data?.items ?? []).map((a) => ({
          value: String(a.id),
          label: a.name,
          // Carry portfolio_id forward so handleAssetChange can back-fill it.
          portfolioId: (a as { portfolio_id?: number }).portfolio_id,
        }));
        if (search.trim()) {
          const needle = search.trim().toLowerCase();
          options = options.filter((o) => o.label.toLowerCase().includes(needle));
        }
        return { options, hasMore: (data?.total ?? 0) > page * SELECT_PAGE_SIZE };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [values.portfolioId, values.companyId],
  );

  const handleCompanyChange = useCallback(
    (value: string, label: string) => {
      setFieldValue("companyId", value);
      setFieldValue("companyLabel", label);
      if (values.portfolioId) {
        setFieldValue("portfolioId", "");
        setFieldValue("portfolioLabel", "");
      }
      if (values.assetId) {
        setFieldValue("assetId", "");
        setFieldValue("assetLabel", "");
      }
    },
    [setFieldValue, values.portfolioId, values.assetId],
  );

  const handlePortfolioChange = useCallback(
    (value: string, label: string, option?: BaseOption) => {
      setFieldValue("portfolioId", value);
      setFieldValue("portfolioLabel", label);
      if (!values.companyId && option?.companyId) {
        setFieldValue("companyId", String(option.companyId));
        setFieldValue("companyLabel", (option.companyName as string) ?? "");
      }
      if (values.assetId) {
        setFieldValue("assetId", "");
        setFieldValue("assetLabel", "");
      }
    },
    [setFieldValue, values.companyId, values.assetId],
  );

  const handleAssetChange = useCallback(
    async (value: string, label: string, option?: BaseOption) => {
      setFieldValue("assetId", value);
      setFieldValue("assetLabel", label);

      // When Asset is picked without Portfolio set, back-fill Portfolio and
      // (if also empty) Company from the asset's parent portfolio.
      const optionPortfolioId = option?.portfolioId;
      if (!value || values.portfolioId || optionPortfolioId == null) return;
      try {
        const { data: portfolio } = await PortfoliosApi.getCurrent(
          Number(optionPortfolioId),
        );
        setFieldValue("portfolioId", String(portfolio.id));
        setFieldValue("portfolioLabel", portfolio.name);
        if (!values.companyId) {
          setFieldValue("companyId", String(portfolio.company_id));
          setFieldValue("companyLabel", portfolio.company_name);
        }
      } catch {
        // Best-effort: leave portfolio/company as-is if the fetch fails.
      }
    },
    [setFieldValue, values.portfolioId, values.companyId],
  );

  const handleSelectChange = useCallback(
    (field: keyof DeviceFormValues) => (value: string) => {
      setFieldValue(field, value);
      setFieldTouched(field, true, false);
    },
    [setFieldValue, setFieldTouched],
  );

  const handleDeviceProfileChange = useCallback(
    (value: string) => {
      setFieldValue("deviceProfileKey", value);
      setFieldTouched("deviceProfileKey", true, false);
      if (isAppKeyAutofillModel(value)) {
        setFieldValue("appKey", DEVICE_APP_KEY_DEFAULT);
      }
    },
    [setFieldValue, setFieldTouched],
  );

  const handleAppKeyChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue("appKey", sanitizeAppKeyInput(e.target.value));
    },
    [setFieldValue],
  );

  const handleDateChange = useCallback(
    (value: string) => {
      setFieldValue("lastCalibrationDate", value);
      setFieldTouched("lastCalibrationDate", true, false);
    },
    [setFieldValue, setFieldTouched],
  );

  const handleCertificateChange = useCallback(
    (file: File | null) => setFieldValue("certificate", file),
    [setFieldValue],
  );

  const handleEuiPhotoClick = useCallback(() => {
    if (isLoading) return;
    euiPhotoRef.current?.click();
  }, [isLoading]);

  const handleEuiPhotoChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      setFieldTouched("devEui", true, false);

      const isAllowedType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
      if (!isAllowedType || file.size > EUI_IMAGE_MAX_BYTES) {
        setFieldError("devEui", EUI_INVALID_FILE_MSG);
        return;
      }

      try {
        const { data } = await DevicesApi.recognizeEui(file);
        if (data?.dev_eui) {
          setFieldValue("devEui", data.dev_eui.toUpperCase());
          setFieldError("devEui", undefined);
        } else {
          setFieldError("devEui", EUI_OCR_FAIL_MSG);
        }
      } catch {
        setFieldError("devEui", EUI_OCR_FAIL_MSG);
      }
    },
    [setFieldValue, setFieldError, setFieldTouched],
  );

  const handleAppKeyPhotoClick = useCallback(() => {
    if (isLoading) return;
    appKeyPhotoRef.current?.click();
  }, [isLoading]);

  const handleAppKeyPhotoChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;

      setFieldTouched("appKey", true, false);

      const isAllowedType = ["image/jpeg", "image/png", "image/webp"].includes(file.type);
      if (!isAllowedType || file.size > EUI_IMAGE_MAX_BYTES) {
        setFieldError("appKey", EUI_INVALID_FILE_MSG);
        return;
      }

      try {
        const { data } = await DevicesApi.recognizeAppKey(file);
        if (data?.app_key) {
          setFieldValue("appKey", data.app_key.replace(/-/g, "").toUpperCase());
          setFieldError("appKey", undefined);
        } else {
          setFieldError("appKey", APP_KEY_OCR_FAIL_MSG);
        }
      } catch {
        setFieldError("appKey", APP_KEY_OCR_FAIL_MSG);
      }
    },
    [setFieldValue, setFieldError, setFieldTouched],
  );

  const lockedTooltipHandlers = !isCreate
    ? {
        onMouseEnter: (e: React.MouseEvent<HTMLDivElement>) =>
          showTooltip(e.currentTarget, LOCKED_FIELD_TOOLTIP),
        onMouseLeave: hideTooltip,
      }
    : {};

  return (
    <div ref={formRef} className={styles.content}>
      <Form className={styles.form} onSubmit={handleSubmit}>
        <fieldset className={styles.fieldset}>
          <p className={styles.fieldsetTitle}>Location</p>

          {isAdmin && (
            <AsyncSearchableSelect
              label="Company"
              placeholder="Select company"
              value={values.companyId}
              selectedLabel={values.companyLabel}
              onSearch={searchCompanies}
              onChange={handleCompanyChange}
              error={touched.companyId ? (errors.companyId as string | undefined) : undefined}
              isRequired
              isDisabled={isLoading}
            />
          )}

          <AsyncSearchableSelect
            label="Portfolio"
            placeholder="Select portfolio"
            value={values.portfolioId}
            selectedLabel={values.portfolioLabel}
            onSearch={searchPortfolios}
            onChange={handlePortfolioChange}
            error={touched.portfolioId ? (errors.portfolioId as string | undefined) : undefined}
            isRequired
            isDisabled={isLoading}
          />

          <AsyncSearchableSelect
            label="Asset"
            placeholder="Select asset"
            value={values.assetId}
            selectedLabel={values.assetLabel}
            onSearch={searchAssets}
            onChange={handleAssetChange}
            error={touched.assetId ? (errors.assetId as string | undefined) : undefined}
            isRequired
            isDisabled={isLoading}
          />

          <TextInput
            id="installationLocation"
            name="installationLocation"
            label="Installation location"
            placeholder="Installation location"
            value={values.installationLocation}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.installationLocation ? errors.installationLocation : undefined}
            hint="Enter the name of the location where the sensor is installed. For example: the right corner of the room."
            isDisabled={isLoading}
            maxLength={100}
          />
        </fieldset>

        <fieldset className={styles.fieldset}>
          <p className={styles.fieldsetTitle}>Device details</p>

          <div {...lockedTooltipHandlers}>
            <SearchableSelect
              label="Device type and model"
              placeholder="Select device type and model"
              value={values.deviceProfileKey}
              onChange={handleDeviceProfileChange}
              options={profileOptions}
              error={touched.deviceProfileKey ? errors.deviceProfileKey : undefined}
              isRequired
              isDisabled={isLoading || !isCreate}
            />
          </div>

          <div {...lockedTooltipHandlers}>
            <TextInput
              id="devEui"
              name="devEui"
              label="Device EUI"
              placeholder="EUI"
              value={values.devEui}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.devEui ? errors.devEui : undefined}
              hint="Enter the unique identifier from the device label."
              isRequired
              isDisabled={isLoading || !isCreate}
              maxLength={DEVICE_EUI_LENGTH}
              afterIcon={
                isCreate ? (
                  <button
                    type="button"
                    className={styles.euiButton}
                    onClick={handleEuiPhotoClick}
                    onMouseEnter={(e) => showTooltip(e.currentTarget, EUI_UPLOAD_TOOLTIP)}
                    onMouseLeave={hideTooltip}
                    onFocus={(e) => showTooltip(e.currentTarget, EUI_UPLOAD_TOOLTIP)}
                    onBlur={hideTooltip}
                    aria-label="Upload a photo of the EUI label"
                    disabled={isLoading}
                  >
                    <ClipboardIcon />
                  </button>
                ) : undefined
              }
            />
          </div>

          <div {...lockedTooltipHandlers}>
            <TextInput
              id="appKey"
              name="appKey"
              label="Device AppKey"
              placeholder="AppKey"
              value={values.appKey}
              onChange={handleAppKeyChange}
              onBlur={handleBlur}
              error={touched.appKey ? errors.appKey : undefined}
              hint="Found AppKey on the device label or in the delivery documentation provided by the manufacturer."
              isRequired
              isDisabled={isLoading || !isCreate}
              afterIcon={
                isCreate ? (
                  <button
                    type="button"
                    className={styles.euiButton}
                    onClick={handleAppKeyPhotoClick}
                    onMouseEnter={(e) => showTooltip(e.currentTarget, APP_KEY_UPLOAD_TOOLTIP)}
                    onMouseLeave={hideTooltip}
                    onFocus={(e) => showTooltip(e.currentTarget, APP_KEY_UPLOAD_TOOLTIP)}
                    onBlur={hideTooltip}
                    aria-label="Upload a photo of the AppKey label"
                    disabled={isLoading}
                  >
                    <ClipboardIcon />
                  </button>
                ) : undefined
              }
            />
          </div>

          <Select
            label="Reporting Interval"
            placeholder="Select reporting interval"
            value={values.reportingInterval}
            onChange={handleSelectChange("reportingInterval")}
            options={reportingOptions}
            error={touched.reportingInterval ? errors.reportingInterval : undefined}
            isRequired
            isDisabled={isLoading}
          />

          {isCreate && (
            <>
              <input
                ref={euiPhotoRef}
                type="file"
                accept={EUI_IMAGE_ACCEPT}
                style={{ display: "none" }}
                onChange={handleEuiPhotoChange}
              />
              <input
                ref={appKeyPhotoRef}
                type="file"
                accept={EUI_IMAGE_ACCEPT}
                style={{ display: "none" }}
                onChange={handleAppKeyPhotoChange}
              />
            </>
          )}
        </fieldset>

        <fieldset className={styles.fieldset}>
          <p className={styles.fieldsetTitle}>Calibration schedule</p>

          {isCreate && (
            <DatePicker
              label="Date of last calibration"
              placeholder="DD.MM.YYYY"
              value={values.lastCalibrationDate}
              onChange={handleDateChange}
              error={touched.lastCalibrationDate ? errors.lastCalibrationDate : undefined}
              maxDate={new Date()}
              isRequired
              isDisabled={isLoading}
            />
          )}

          <Select
            label="Calibration interval"
            placeholder="Select calibration interval"
            value={values.calibrationInterval}
            onChange={handleSelectChange("calibrationInterval")}
            options={CALIBRATION_OPTIONS}
            error={touched.calibrationInterval ? errors.calibrationInterval : undefined}
            isRequired
            isDisabled={isLoading}
          />

          {isCreate && (
            <CertificateUpload
              value={values.certificate}
              onChange={handleCertificateChange}
              isDisabled={isLoading}
            />
          )}
        </fieldset>

        {submitError && (
          <div role="alert" className={styles.submitError}>
            {submitError}
          </div>
        )}

        <div className={styles.buttons}>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isCreate ? "Add" : "Update"}
          </Button>
        </div>
      </Form>

      {tooltip &&
        createPortal(
          <div
            role="tooltip"
            className={styles.tooltip}
            style={{
              left: tooltip.left,
              top: tooltip.top,
              transform:
                tooltip.placement === "right" ? "translateY(-50%)" : "translateX(-100%)",
            }}
          >
            {tooltip.content}
          </div>,
          document.querySelector("dialog[open]") ?? document.body,
        )}
    </div>
  );
};

const ModalCreateDeviceContent: FC<PropTypes> = ({
  onClose,
  onSubmit,
  onUpdate,
  mode = "create",
  description,
  initialData,
  isLoading,
  submitError,
}) => {
  const isCreate = mode === "create";

  const authUser = useAppSelector((state: RootStore) => state.auth.user);
  const isAdmin = authUser?.role === UserRole.ADMINISTRATOR;

  const initialValues = useMemo<DeviceFormValues>(
    () => ({
      ...defaultInitialValues,
      ...(!isAdmin && authUser?.company_id != null
        ? { companyId: String(authUser.company_id), companyLabel: "" }
        : {}),
      ...initialData,
    }),
    [initialData, isAdmin, authUser?.company_id],
  );

  const validationSchema = useMemo(
    () => buildValidationSchema(isCreate, isAdmin),
    [isCreate, isAdmin],
  );

  const handleCancel = useCallback(() => onClose?.(), [onClose]);

  return (
    <>
      {!!description && <p className={styles.modalDescription}>{description}</p>}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values) => {
          const normalized = {
            ...values,
            installationLocation: values.installationLocation.trim(),
            devEui: values.devEui.trim().toUpperCase(),
            appKey: values.appKey.trim().replace(/-/g, "").toUpperCase(),
          };
          if (isCreate) {
            onSubmit?.(normalized);
          } else {
            onUpdate?.(values.devEui, normalized);
          }
        }}
      >
        <FormFields onCancel={handleCancel} mode={mode} isLoading={isLoading} submitError={submitError} />
      </Formik>
    </>
  );
};

export default ModalCreateDeviceContent;
